import React, { useState } from 'react';
import { Col, Row } from 'antd';
import { Title, Tabs } from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import { formatDateRecentTime } from 'utils/misc';
import { Rule, RuleRunStatus } from 'types/dt-graphql-apollo-v2';
import { HorizontalDivider } from 'components';
import { sortRunsByDate } from 'utils/data-validation';
import { RunStatistics } from './components/RunStatistics';
import { RuleDetails } from './components/RuleDetails';
import { RuleHistory } from './components/RuleHistory';

type TabKeys = 'ruleDetails' | 'ruleHistory';

export const RuleDetailsSegment = ({
  selectedRule,
}: {
  selectedRule?: Rule;
}) => {
  const [activeTabKey, setActiveTabKey] = useState<TabKeys>('ruleDetails');
  const sortedRuns = sortRunsByDate(
    (selectedRule?.ruleRunStatus?.items as RuleRunStatus[]) || []
  );

  const [currentRun, pastRun] = sortedRuns;

  const handleTabChange = (newKey: TabKeys) => {
    setActiveTabKey(newKey);
  };

  const renderTabContent = () => {
    if (activeTabKey === 'ruleHistory')
      return <RuleHistory ruleRuns={sortedRuns} />;

    return <RuleDetails selectedRule={selectedRule} />;
  };

  return (
    <DetailsView>
      <RuleInformation>
        <StyledHeaderColumn>
          <Row>
            <StyledOverline>
              {currentRun?.runAt && formatDateRecentTime(currentRun?.runAt)}
            </StyledOverline>
          </Row>
          <Row>
            <StyledTitle level={3}>
              {selectedRule?.name}
              {selectedRule?.customerDefinedId &&
                ` - ${selectedRule.customerDefinedId}`}
            </StyledTitle>
          </Row>
          <StyledErrorMessageCol>
            <StyledOverline>Error Message</StyledOverline>
            <Title level={6}>{selectedRule?.errorMessage || '-'}</Title>
          </StyledErrorMessageCol>
        </StyledHeaderColumn>
      </RuleInformation>

      <StyledColumn>
        <RunStatistics selectedRun={currentRun} pastRun={pastRun} />
      </StyledColumn>

      <StyledTabs
        activeKey={activeTabKey}
        onChange={(key: any) => handleTabChange(String(key) as TabKeys)}
      >
        <Tabs.Tab tabKey="ruleDetails" label="Rule details" />
        <Tabs.Tab tabKey="ruleHistory" label="Run history" />
      </StyledTabs>

      <HorizontalDivider />

      {renderTabContent()}
    </DetailsView>
  );
};

const DetailsView = styled.div`
  height: calc(100vh - 8rem);
  border-left: solid 1px var(--cogs-border-default);
  background: var(--cogs-white);
  overflow: auto;
  transition: all 250ms linear;
  padding: 0 1rem;
`;

const StyledTitle = styled(Title)`
  line-height: 32px;
`;

const RuleInformation = styled.div`
  margin: 1rem;
`;

const StyledColumn = styled(Col)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  line-height: 24px;
`;

const StyledOverline = styled.div`
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  font-family: 'Inter', sans-serif;
  color: #595959;
`;

const StyledHeaderColumn = styled(StyledColumn)`
  gap: 0;
`;

const StyledErrorMessageCol = styled(Col)`
  gap: 0.2rem;
  margin-top: 1rem;
`;

const StyledTabs = styled(Tabs)`
  .rc-tabs-nav {
    border-bottom: 0;
    flex-wrap: wrap-reverse;
    overflow: hidden;
  }
  .rc-tabs-tab {
    font-weight: 500;
  }
  .rc-tabs-extra-content {
    flex: auto;
  }
`;
